/* oxlint-disable react/jsx-no-bind */
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb } from "@fortawesome/sharp-regular-svg-icons";
import UploadDragDrop from "@/components/forms/UploadDragDrop.tsx";
import ReferralForm, { ReferralFormInput } from "./ReferralForm";
import { gql } from "@/__generated__";
import Loader from "@/components/shared/Loader";
import { uploadFiles } from "@/utils/fileRequests";
import InlineBanner from "@/components/shared/InlineBanner";
import {
  ProviderWithFacilitiesQuery,
  ProviderWithFacilitiesQueryVariables,
  ReferralInput,
  SubDenialReason,
} from "@/__generated__/graphql";
import Navbar from "@/components/layout/Navbar";
import { PageError } from "@/components/PageError";

import styles from "./refer-patient.module.scss";
import "../../styles/components/landing-page.scss";
import {
  Condition,
  isGlp1Condition,
} from "shared/src/data/medication-condition-pairs";

export type ConvertedFile = {
  fileName: string;
  files: File[];
};

export type ConversionResponse = {
  successfulConversions: ConvertedFile[];
  conversionFailedFilenames: string[]; // the filenames of the files that errored outs
};

export const GET_PROVIDER_WITH_FACILITIES = gql(`
  query ProviderWithFacilities {
    provider {
      providerId
      facilities {
        providerFacilityId
        facilityName
        facilityShortName
      }
    }
  }
`);

export const SAVE_REFERRAL_MUTATION = gql(`
  mutation SaveReferral($referral: ReferralInput!) {
    saveReferral(referral: $referral) {
      appealId
      referralId
      denialLetterLinks {
        fileExtension
        uploadUrl
      }
      medLetterLinks {
        fileExtension
        uploadUrl
      }
    }
  }
`);

export const subDenialReasonOptionsByCondition: Record<
  Condition.OBESITY | Condition.TYPE_2_DIABETES,
  { [key in SubDenialReason]?: string }
> = {
  [Condition.OBESITY]: {
    [SubDenialReason.BmiDoesntMeetCriteria]:
      "BMI doesn’t meet  payer's criteria",
    [SubDenialReason.WeightLossProgramDoesntMeetCriteria]:
      "Weight loss program doesn’t meet criteria",
    [SubDenialReason.WeightLossCareNotCoveredAtAll]:
      "Weight loss care not covered at all",
    [SubDenialReason.MedicationNotCoveredAtAll]:
      "Medication not covered at all",
    [SubDenialReason.MedicationNotCoveredForMyDiagnosis]:
      "Medication not covered for my diagnosis",
    [SubDenialReason.MustTryOtherMedicationsFirst]:
      "Must try other medications first",
    [SubDenialReason.DidntLoseEnoughWeight]: "Didn’t lose enough weight",
    [SubDenialReason.LostTooMuchWeight]: "Lost too much weight",
  },
  [Condition.TYPE_2_DIABETES]: {
    [SubDenialReason.A1CDoesntMeetCriteria]:
      "A1c doesn't meet payer's criteria",
    [SubDenialReason.ExerciseOrDietDoesntMeetCriteria]:
      "Exercise or diet doesn't meet plan criteria",
    [SubDenialReason.MustTryOtherMedicationsFirst]:
      "Must try other medications first",
    [SubDenialReason.MedicationNotConsideredEffective]:
      "Medication was not considered effective",
    [SubDenialReason.MedicationNotCoveredAtAll]:
      "Medication not covered at all by my plan",
  },
};

const ReferPatient = () => {
  const [letterErrors, setLetterErrors] = useState<string[]>([]);
  const [letterFiles, setLetterFiles] = useState<File[]>([]);
  const navigate = useNavigate();

  const {
    loading: providerFacilitiesQueryLoading,
    data: providerFacilitiesData,
    error: providerFacilitiesError,
  } = useQuery<
    ProviderWithFacilitiesQuery,
    ProviderWithFacilitiesQueryVariables
  >(GET_PROVIDER_WITH_FACILITIES, {
    fetchPolicy: "no-cache",
  });

  const providerFacilities = useMemo(() => {
    if (providerFacilitiesData?.provider?.facilities) {
      return providerFacilitiesData.provider.facilities.sort(
        (providerFacilityA, providerFacilityB) =>
          providerFacilityA.facilityName.localeCompare(
            providerFacilityB.facilityName,
          ),
      );
    }
    return [];
  }, [providerFacilitiesQueryLoading, providerFacilitiesData]);

  const [saveReferral, { loading, error: saveError }] = useMutation(
    SAVE_REFERRAL_MUTATION,
  );

  const getSubDenialReason = (
    subDenialReasonValue?: string,
    condition?: string | null,
  ): SubDenialReason | undefined => {
    if (!isGlp1Condition(condition) || !subDenialReasonValue) {
      return undefined;
    }

    const subDenialReasonEntry = Object.entries(
      subDenialReasonOptionsByCondition[condition],
    ).find(([, value]) => value === subDenialReasonValue);

    if (!subDenialReasonEntry) {
      // oxlint-disable-next-line no-console
      console.error("Invalid sub denial reason:", subDenialReasonValue);

      return undefined;
    }

    return subDenialReasonEntry[0] as SubDenialReason;
  };

  const handleSubmit = async (formData: ReferralFormInput) => {
    let referralVars: ReferralInput = {
      firstName: formData.firstName as string,
      lastName: formData.lastName as string,
      patientEmail: formData.patientEmail as string,
      phoneNumber: formData.phoneNumber as string,
      hasConsentedToSMSNotifications:
        formData.hasConsentedToSMSNotifications as boolean,
      dateOfBirth: formData.dateOfBirth,
      state: formData.state as string,
      condition: formData.condition as string,
      medication: formData.medication as string,
      denialReason: formData.denialReason as string,
      message: formData.message as string,
      subDenialReason: getSubDenialReason(
        formData.subDenialReason as string,
        formData.condition,
      ),
      providerFacilityId: formData.providerFacilityId,
    };

    if (letterFiles.length > 0) {
      referralVars = {
        ...referralVars,
        denialLetterExtensions: letterFiles.map(
          (file) => file.name.split(".").pop() ?? "",
        ),
      };
    }

    const { data } = await saveReferral({
      variables: {
        referral: referralVars,
      },
    });

    if (data) {
      const letterLinks = data.saveReferral.denialLetterLinks;
      if (
        letterFiles.length > 0 &&
        letterLinks?.length === letterFiles.length
      ) {
        await uploadFiles(letterFiles, letterLinks);
      }

      const { appealId } = data.saveReferral;

      navigate(`/refer-patient-documents?appealId=${appealId}`);
    }
  };

  async function handleLetterUpload(uploadedFiles: File[]) {
    if (uploadedFiles) {
      setLetterErrors([]);
      setLetterFiles(uploadedFiles);
    }
    return undefined;
  }

  function handleBadLetterUpload(error: string[]) {
    setLetterErrors(error);
  }

  function handleLetterClose(message: string) {
    setLetterErrors(letterErrors.filter((error) => error !== message));
  }

  if (providerFacilitiesError) {
    return <PageError />;
  }

  if (providerFacilitiesQueryLoading) {
    return (
      <Navbar titleText="Refer patient">
        <div className={styles.loadingContainer}>
          <Loader color="black" width="36" height="36" />
        </div>
      </Navbar>
    );
  }

  return (
    <Navbar titleText="Refer patient">
      <div className={styles.pageContainer}>
        <div className={styles.formContainer}>
          <h2 className={styles.formHeader}>{"Denial info"}</h2>
          <div className={styles.formSubheader}>
            <p className={styles.formSubheader__text}>
              {
                "Tell us about your patient and we’ll send them an email inviting them to Claimable. See a sample "
              }
            </p>
            <p className={styles.formSubheader__text}>
              <a
                className={styles.formSubheader__link}
                target="_blank"
                href="/images/product_illustrations/sample_email.png"
              >
                {"email here."}
              </a>
            </p>
          </div>
          <hr className={styles.sectionDivider} />
          {saveError && (
            <>
              <InlineBanner
                type="error"
                message={saveError.message}
                key={saveError.message}
              />
              <br />
            </>
          )}
          <ReferralForm
            providerFacilities={providerFacilities}
            formId="referralForm"
            onSubmit={handleSubmit}
          />
        </div>

        <div className={styles.uploadsContainer}>
          {letterErrors.map((error) => (
            <>
              <InlineBanner
                type="error"
                message={error}
                dismissable
                onDismiss={handleLetterClose}
                key={error}
              />
              <br />
            </>
          ))}
          <UploadDragDrop
            sectionHeader="Upload denial letter"
            headerText="Drag your files here or click to upload"
            subText="Ensure all pages of your denial letter are included. Maximum file size 20MB"
            onDragDropAction={handleLetterUpload}
            onBadItemUpload={handleBadLetterUpload}
          />
          <div className={styles.disclaimerContainer}>
            <div className={styles.uploadDisclaimer}>
              <div className={styles.icon}>
                <FontAwesomeIcon icon={faLightbulb} title="Information" />
              </div>
              <div className={styles.copy}>
                <p className={styles.title}>{"Why this matters"}</p>
                <p>
                  {
                    "We pull relevant information from the denial letter to give your patient a head start. The denial letter is not sent with the appeal. While strongly recommended, it's optional."
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={classnames(
          "send-referral-button-container",
          styles.sendReferralButton,
        )}
      >
        <button
          type="submit"
          form="referralForm"
          disabled={loading}
          className={classnames("btn", "dark_double_right_arrow", {
            loading_button: loading,
          })}
        >
          {loading ? (
            <div className="loading_indicator_container">
              <div className="loading_indicator_text">{"Loading..."}</div>
              <div className="loading_indicator">
                <Loader color="loader_light_volt" width="36" height="36" />
              </div>
            </div>
          ) : (
            <>
              {"Send Referral"}
              <span className="arrow" />
            </>
          )}
        </button>
      </div>
    </Navbar>
  );
};

export default ReferPatient;
